import React from "react";

/**
 * @typedef {Object} Step
 * @property {string} title - Kort etikett som visas under cirkeln (t.ex. "Välj fil")
 * @property {string} [description] - Valfri sub-text under titeln (1 rad max)
 */

/**
 * Stepper — visar progress i en flerstegs-wizard. Stilen matchar portalens
 * resterande design (Rubik, airpark-green, gråskala på inaktiva steg).
 * Komponenten är "kontrollerad" — caller äger active-state.
 *
 * @param {Object} props
 * @param {Step[]} props.steps               Definition av varje steg
 * @param {number} props.activeStep          0-baserat index för aktivt steg
 * @param {Function} [props.onStepClick]     Klick på tidigare steg (för att hoppa bak). Skicka null/undefined för att stänga av.
 * @param {string} [props.className]
 */
const Stepper = ({ steps, activeStep, onStepClick, className = "" }) => {
  return (
    <div className={`flex w-full items-start ${className}`}>
      {steps.map((step, idx) => {
        const isDone = idx < activeStep;
        const isActive = idx === activeStep;
        const isUpcoming = idx > activeStep;
        const canClick = !!onStepClick && idx < activeStep; // bara tidigare steg klickbara

        const circleClasses = isActive
          ? "bg-airpark-green text-white border-airpark-green"
          : isDone
            ? "bg-airpark-green-background border-airpark-green text-airpark-green"
            : "bg-white border-airpark-gray-400 text-airpark-gray-500";

        const labelClasses = isActive
          ? "text-airpark-oil-500 font-medium"
          : isDone
            ? "text-airpark-green"
            : "text-airpark-gray-500";

        const connectorClasses = isDone
          ? "bg-airpark-green"
          : "bg-airpark-gray-300";

        return (
          <React.Fragment key={idx}>
            <div className="flex flex-col items-center min-w-[80px]">
              <button
                type="button"
                onClick={canClick ? () => onStepClick(idx) : undefined}
                disabled={!canClick}
                className={`flex items-center justify-center w-9 h-9 rounded-full border-2 transition-all ${circleClasses} ${canClick ? "cursor-pointer hover:opacity-80" : "cursor-default"}`}
              >
                {isDone ? (
                  <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <span className="text-sm font-medium">{idx + 1}</span>
                )}
              </button>
              <span className={`mt-2 text-sm text-center ${labelClasses}`}>{step.title}</span>
              {step.description ? (
                <span className="mt-1 text-xs text-airpark-gray-500 text-center">{step.description}</span>
              ) : null}
            </div>

            {idx < steps.length - 1 ? (
              <div className={`flex-1 h-[2px] mt-[18px] mx-2 transition-colors ${connectorClasses}`} />
            ) : null}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default Stepper;
